import { baseApi } from "./baseApi";

// admin view of who can be notified and how (backend /api/admin/notifications/reach,
// which joins every site user with messenger's telegram links, push devices and receipts)

export type TelegramState = "ok" | "muted" | "blocked" | "none";
export type PushState = "ok" | "lost" | "none";
export type ReachIssue =
  | "tg_blocked"
  | "tg_muted"
  | "tg_service_muted"
  | "tg_link_by_username"
  | "push_lost"
  | "permission_denied"
  | "permission_without_subscription"
  | "installed_without_push"
  | "push_unconfirmed";

export interface ReachChannelLeg {
  status: "sent" | "failed" | "skipped" | string;
  reason?: string;
}

export interface ReachPushLeg extends ReachChannelLeg {
  devices: number;
  accepted: number;
}

export interface ReachStats {
  total: number;
  delivered: number;
  viaTelegram: number;
  viaPush: number;
  shown: number;
  clicked: number;
  suppressed: number;
}

export interface ReachDevice {
  installId: string;
  platform: string | null;
  browser: string | null;
  standalone: boolean;
  lastStandaloneAt: string | null;
  permission: string;
  pushSupported: boolean;
  hasSubscription: boolean;
  lastSeenAt: string | null;
}

export interface ReachPushSubscription {
  id: string;
  platform: string;
  browser: string;
  active: boolean;
  createdAt: string | null;
  prunedAt: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastFailureReason: string | null;
  lastReceiptAt: string | null;
}

export interface ReachUser {
  userId: string;
  username: string;
  role: string | null;
  userEmoji: string | null;
  signedUpAt: string | null;
  lastSignedAt: string | null;
  reachable: boolean;
  telegramState: TelegramState;
  pushState: PushState;
  activePushDevices: number;
  appInstalled: boolean;
  lastStandaloneAt: string | null;
  permission: "granted" | "default" | "denied" | "unsupported" | "unknown";
  issues: ReachIssue[];
  telegram: {
    username: string | null;
    firstName: string | null;
    siteUsername: string | null;
    connectedAt: string | null;
    lastActiveAt: string | null;
    globalMute: boolean;
    mutedServices: string[];
    blockedAt: string | null;
  } | null;
  devices: ReachDevice[];
  pushSubscriptions: ReachPushSubscription[];
  stats: ReachStats;
  last: {
    at: string | null;
    code: string | null;
    preview: string;
    delivered: boolean;
    tg: ReachChannelLeg | null;
    push: ReachPushLeg | null;
    shownAt: string | null;
    clickedAt: string | null;
  } | null;
}

export interface ReachSummary {
  users: number;
  reachable: number;
  telegramOnly: number;
  pushOnly: number;
  both: number;
  none: number;
  appInstalled: number;
  permissionDenied: number;
  withIssues: number;
}

export interface ReachReportResponse {
  success: boolean;
  data?: {
    days: number;
    since: string | null;
    summary: ReachSummary;
    users: ReachUser[];
    orphans: Array<{ userId: string; telegramUsername: string | null; siteUsername: string | null }>;
  };
  error?: string;
}

export interface DeliveryRow {
  _id: string;
  userId: string;
  code?: string;
  kind: "notify" | "broadcast" | "test";
  preview: string;
  tg: ReachChannelLeg;
  push: ReachPushLeg;
  delivered: boolean;
  shownAt?: string;
  clickedAt?: string;
  suppressedAt?: string;
  createdAt: string;
}

export interface DeliveriesResponse {
  success: boolean;
  data?: DeliveryRow[];
  error?: string;
}

export const notificationReachApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotificationReach: builder.query<ReachReportResponse, { days?: number } | void>({
      query: (args) => ({
        url: "/admin/notifications/reach",
        params: args && args.days ? { days: args.days } : undefined,
      }),
    }),
    getUserDeliveries: builder.query<DeliveriesResponse, { userId: string; limit?: number }>({
      query: ({ userId, limit }) => ({
        url: `/admin/notifications/reach/${encodeURIComponent(userId)}/deliveries`,
        params: limit ? { limit } : undefined,
      }),
    }),
  }),
});

export const { useGetNotificationReachQuery, useLazyGetUserDeliveriesQuery } = notificationReachApi;
